import type { Context, Next } from "hono";
import * as defaultAuthSchema from "./auth-schema.js";
import type { AuthHookContext, AuthUserLike, GencowAuthConfig, GencowConfig } from "@gencow/core";
import { sql } from "drizzle-orm";
import { isTrustedOAuthCallbackURL, resolveOAuthCallbackURL } from "./auth-oauth-callback.js";
import { consumeOAuthExchangeCode, createOAuthExchangeCode } from "./auth-oauth-exchange-store.js";
import { resolveOAuthReturnMode, type OAuthReturnMode } from "./auth-oauth-return-mode.js";
import { presentSafeAuthSessionUser } from "./auth-session-presenter.js";
import { buildGencowBetterAuthOptions, getAuthUserAdditionalFieldNames } from "./better-auth-options.js";
import { listEnabledSocialProviderIds, resolveSocialAuthConfig } from "./auth-social-config.js";
import { resolveBetterAuthTrustedOrigins } from "./better-auth-trusted-origins.js";
import { getPublicAuthErrorMessage, sanitizePublicAuthResponsePayload } from "./auth-error-sanitization.js";
import { redactAuthLogValue } from "./auth-log-redaction.js";
import {
  buildSessionLookupHeaders,
  resolveRequestSession,
  sessionTokenToCookieHeader,
  type SessionLookupResult,
} from "./auth-session-resolution.js";
import {
  createOAuthInviteReservation,
  finalizeOAuthInviteReservation,
  releaseOAuthInviteReservation,
  type OAuthInviteReservation,
} from "./auth-oauth-invite-reservation.js";
import {
  consumeOAuthSignUpHandoff,
  createOAuthSignUpHandoff,
  releaseOAuthSignUpHandoff,
} from "./auth-oauth-signup-handoff.js";
import {
  copySessionUserFieldsForRuntimeIdentity,
  lookupRuntimeAuthUser,
  type RuntimeAuthUser,
} from "./better-auth-identity.js";
import { enforceAuthRateLimit } from "./auth-rate-limit.js";
import { isInviteOnlyMode, releaseInviteCode, reserveInviteCode } from "./invite-code.js";
import { resolveDelegatedNodeProxyUser } from "./server-node-proxy-delegated-auth.js";

export { resolveBetterAuthTrustedOrigins } from "./better-auth-trusted-origins.js";

type RuntimeAuthSchema = Record<string, unknown>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let authInstance: any = null;

let authInitPromise: Promise<any> | null = null;
let hasEmailVerification = false;
let dbInstance: any = null;

let pendingAuthConfig: GencowAuthConfig | undefined;
let pendingProjectConfig: GencowConfig | undefined;
let runtimeAuthSchema: RuntimeAuthSchema = defaultAuthSchema;

let capturedSecret: string | undefined;
let capturedBaseURL: string | undefined;

function buildAuthHookContext(request?: unknown): AuthHookContext {
  return { db: dbInstance, request };
}

async function runAuthEventHook(label: string, fn: () => Promise<void>): Promise<void> {
  try {
    await fn();
  } catch (error) {
    console.error(`[auth] ${label} hook failed:`, redactAuthLogValue(error));
  }
}

async function runAfterSignUpHook(
  user: AuthUserLike,
  inviteCode: string | null,
  request: unknown,
): Promise<void> {
  const hook = pendingAuthConfig?.events?.afterSignUp;
  if (!hook) return;
  await runAuthEventHook("afterSignUp", () => hook({ user, inviteCode }, buildAuthHookContext(request)));
}

function getCustomUserFieldNames(): string[] {
  return getAuthUserAdditionalFieldNames(pendingAuthConfig);
}
function resolvePlatformBaseDomain(): string | undefined {
  return process.env.BASE_DOMAIN || (process.env.IS_PLATFORM === "true" ? "gencow.app" : undefined);
}

function getOAuthCallbackResolverOptions(requestedCallbackURL?: string) {
  return {
    requestedCallbackURL,
    authConfig: pendingAuthConfig,
    baseURL: capturedBaseURL || `http://localhost:${process.env.PORT || "5456"}`,
    appPublicDomain: process.env.APP_PUBLIC_DOMAIN,
    trustedOrigins: resolveBetterAuthTrustedOrigins({
      baseDomain: resolvePlatformBaseDomain(),
      appPublicDomain: process.env.APP_PUBLIC_DOMAIN,
      capturedBaseURL,
      projectTrustedOrigins: pendingProjectConfig?.trustedOrigins,
      corsOriginsEnv: process.env.CORS_ORIGINS,
      authConfig: pendingAuthConfig,
    }),
  };
}

export function initAuth(
  db: unknown,
  authConfig?: GencowAuthConfig,
  projectConfig?: GencowConfig,
  options: { authSchema?: RuntimeAuthSchema } = {},
) {
  hasEmailVerification = !!authConfig?.emailVerification;
  dbInstance = db;
  pendingAuthConfig = authConfig;
  pendingProjectConfig = projectConfig;
  runtimeAuthSchema = options.authSchema ?? defaultAuthSchema;
  capturedSecret = process.env.BETTER_AUTH_SECRET;
  capturedBaseURL = process.env.BETTER_AUTH_URL;
}

export function resetAuthForTests() {
  authInstance = null;
  authInitPromise = null;
  hasEmailVerification = false;
  dbInstance = null;
  pendingAuthConfig = undefined;
  pendingProjectConfig = undefined;
  runtimeAuthSchema = defaultAuthSchema;
  capturedSecret = undefined;
  capturedBaseURL = undefined;
}

async function ensureAuth(): Promise<any> {
  if (authInstance) return authInstance;
  if (authInitPromise) return authInitPromise;

  if (!dbInstance) {
    throw new Error("[auth] initAuth() not called yet — DB instance required");
  }

  authInitPromise = (async () => {
    try {
      const _t = performance.now();
      const { betterAuth } = await import("better-auth");
      const { drizzleAdapter } = await import("better-auth/adapters/drizzle");
      const { bearer, genericOAuth } = await import("better-auth/plugins");

      const authConfig = pendingAuthConfig;
      const database = drizzleAdapter(dbInstance as Parameters<typeof drizzleAdapter>[0], {
        provider: "pg",
        schema: runtimeAuthSchema,
      });
      const { socialProviders, customOAuthConfig } = resolveSocialAuthConfig(authConfig?.socialProviders);

      authInstance = betterAuth(
        buildGencowBetterAuthOptions({
          authConfig,
          database,
          secret: capturedSecret,
          baseURL: capturedBaseURL,
          port: process.env.PORT,
          authHookContext: buildAuthHookContext,
          runAuthEventHook,
          socialProviders,
          plugins: [
            bearer(),
            ...(customOAuthConfig.length > 0 ? [genericOAuth({ config: customOAuthConfig })] : []),
          ],
          trustedOrigins: resolveBetterAuthTrustedOrigins({
            baseDomain: resolvePlatformBaseDomain(),
            appPublicDomain: process.env.APP_PUBLIC_DOMAIN,
            capturedBaseURL,
            projectTrustedOrigins: pendingProjectConfig?.trustedOrigins,
            corsOriginsEnv: process.env.CORS_ORIGINS,
            authConfig,
          }),
        }),
      );

      console.log(`[auth] better-auth lazy-initialized in ${(performance.now() - _t).toFixed(0)}ms ✓`);
      return authInstance;
    } catch (e) {
      authInitPromise = null;
      console.error(`[auth] ❌ better-auth lazy-init failed:`, redactAuthLogValue(e));
      throw e;
    }
  })();

  return authInitPromise;
}

export function getAuth() {
  return authInstance;
}

export function isAuthLoaded(): boolean {
  return authInstance !== null;
}

import { sign, verify } from "hono/jwt";
import { getSecretKey } from "./crypto.js";

const JWT_ALG = "HS256";
const JWT_EXPIRES_IN_SEC = 5 * 60;

type CurrentUser = RuntimeAuthUser;

function pickCustomUserFields(user: Record<string, unknown>) {
  const custom: Record<string, unknown> = {};
  for (const name of getCustomUserFieldNames()) {
    if (user[name] instanceof Date) {
      custom[name] = user[name].toISOString();
    } else if (user[name] !== undefined) {
      custom[name] = user[name];
    }
  }
  return custom;
}

function toCurrentUser(user: Record<string, unknown>): CurrentUser {
  return {
    ...copySessionUserFieldsForRuntimeIdentity(user),
    ...pickCustomUserFields(user),
    id: String(user.id),
    email: String(user.email),
    name: typeof user.name === "string" ? user.name : undefined,
  };
}

export function betterAuthMiddleware(db?: unknown) {
  return async (c: Context, next: Next) => {
    let currentUser: CurrentUser | null = null;
    let authError: Error | null = null;

    if (db) {
      const authHeader = c.req.header("Authorization");
      if (authHeader?.startsWith("Bearer gk_")) {
        const token = authHeader.substring(7);
        try {
          const rows = await (db as any).execute(
            sql`SELECT "user_id" FROM "cli_tokens" WHERE "token" = ${token} AND "expires_at" > NOW() LIMIT 1`,
          );
          const row = (rows?.rows?.[0] ?? rows?.[0]) as { user_id?: string } | undefined;
          if (row?.user_id) {
            currentUser = await lookupRuntimeAuthUser(db, row.user_id);
          }
        } catch {
        }
      }
      if (!currentUser) {
        currentUser = await resolveDelegatedNodeProxyUser(db, c);
      }
    }

    if (!currentUser) {
      const cookieHeader = c.req.header("cookie") || "";
      const reqAuthHeader = c.req.header("Authorization") || "";
      const hasAuthSignal =
        cookieHeader.includes("better-auth.session_token") ||
        (reqAuthHeader.startsWith("Bearer ") && !reqAuthHeader.startsWith("Bearer gk_"));

      if (hasAuthSignal) {
        await ensureAuth();

        const AUTH_TIMEOUT_MS = 8000;
        try {
          await Promise.race([
            (async () => {
              if (reqAuthHeader.startsWith("Bearer ")) {
                const token = reqAuthHeader.substring(7);
                try {
                  const secret = getSecretKey().toString("base64");
                  const payload = await verify(token, secret, JWT_ALG);

                  if (
                    payload &&
                    typeof payload === "object" &&
                    typeof payload.sub === "string" &&
                    typeof payload.email === "string"
                  ) {
                    currentUser = toCurrentUser({ ...payload, id: payload.sub });
                  }
                } catch (e) {
                  try {
                    const session = await authInstance.api.getSession({
                      headers: new Headers({
                        cookie: sessionTokenToCookieHeader(token),
                      }),
                    });
                    if (session?.user) {
                      currentUser = toCurrentUser(session.user);
                    } else {
                      authError = e instanceof Error ? e : new Error(String(e));
                    }
                  } catch {
                    authError = e instanceof Error ? e : new Error(String(e));
                  }
                }
              }

              if (!currentUser && !authError) {
                if (cookieHeader.includes("better-auth.session_token")) {
                  const session = await authInstance.api.getSession({
                    headers: buildSessionLookupHeaders(c.req.raw.headers, cookieHeader),
                  });
                  if (session?.user) {
                    currentUser = toCurrentUser(session.user);
                  }
                }
              }
            })(),
            new Promise<void>((_, reject) =>
              setTimeout(() => reject(new Error("Auth session timeout")), AUTH_TIMEOUT_MS),
            ),
          ]);
        } catch (e) {
          if (e instanceof Error && e.message === "Auth session timeout") {
            console.warn("[auth] ⚠️ Session check timed out — DB connection pool may be exhausted");
          }
        }
      }
    }

    c.set("auth", {
      getUserIdentity: () => currentUser,
      requireAuth: () => {
        if (authError) throw authError;
        if (!currentUser) throw new Error("Authentication required");
        return currentUser;
      },
    });

    await next();
  };
}

function extractSessionToken(response: Response): string | null {
  return response.headers.get("set-auth-token");
}

function forwardOAuthStateCookieHeaders(c: Context, response: Response) {
  const setCookieHeaders = response.headers.getSetCookie?.() ?? [];
  for (const cookie of setCookieHeaders) {
    if (cookie.includes("better-auth.state=")) {
      c.header("Set-Cookie", cookie, { append: true });
    }
  }

  if (setCookieHeaders.length === 0) {
    const single = response.headers.get("set-cookie");
    if (single?.includes("better-auth.state=")) c.header("Set-Cookie", single, { append: true });
  }
}

function resolveRequestOrigin(c: Context, url: URL): string {
  const forwardedHost = c.req.header("x-forwarded-host")?.split(",")[0]?.trim();
  const host = forwardedHost || c.req.header("host") || url.host;
  const forwardedProto = c.req.header("x-forwarded-proto")?.split(",")[0]?.trim();
  const protocol = forwardedProto || url.protocol.replace(/:$/, "");

  return `${protocol}://${host}`;
}

function callbackMatchesOrigin(callbackURL: string, origin: string): boolean {
  try {
    return new URL(callbackURL).origin === origin;
  } catch {
    return false;
  }
}

function resolvePublicAuthOrigin(c: Context, url: URL, callbackURLs: string[] = []): string {
  const requestOrigin = resolveRequestOrigin(c, url);
  if (callbackURLs.some((callbackURL) => callbackMatchesOrigin(callbackURL, requestOrigin))) {
    return requestOrigin;
  }
  if (capturedBaseURL) return new URL(capturedBaseURL).origin;
  return requestOrigin;
}

type SocialSignInInput = {
  provider?: string;
  callbackURL?: string;
  oauthReturnMode?: OAuthReturnMode | string;
  errorCallbackURL?: string;
  newUserCallbackURL?: string;
  scopes?: string[];
  requestSignUp?: boolean;
  inviteCode?: string;
  idToken?: unknown;
  loginHint?: string;
  additionalData?: Record<string, unknown>;
};

function providerRequiresInviteForExplicitSignUp(provider: string): boolean {
  if (!isInviteOnlyMode()) return false;
  const providerConfig = pendingAuthConfig?.socialProviders?.[provider] as
    | { disableImplicitSignUp?: boolean }
    | undefined;
  return providerConfig?.disableImplicitSignUp === true;
}

function buildOAuthHandoffURL(
  publicOrigin: string,
  callbackURL: string,
  mode: OAuthReturnMode,
  options: { inviteReservationToken?: string; signUpHandoffToken?: string } = {},
): URL {
  const handoffURL = new URL(`${publicOrigin}/api/auth/oauth/handoff`);
  handoffURL.searchParams.set("callbackURL", callbackURL);
  handoffURL.searchParams.set("mode", mode);
  if (options.inviteReservationToken) {
    handoffURL.searchParams.set("inviteReservation", options.inviteReservationToken);
  }
  if (options.signUpHandoffToken) {
    handoffURL.searchParams.set("signupHandoff", options.signUpHandoffToken);
  }
  return handoffURL;
}

async function startSocialSignIn(c: Context, url: URL, input: SocialSignInInput): Promise<Response> {
  if (!input.provider) {
    return c.json({ error: "Provider is required" }, 400);
  }
  const callbackURL = resolveOAuthCallbackURL(getOAuthCallbackResolverOptions(input.callbackURL));
  if (!callbackURL) return c.json({ error: "Invalid callbackURL" }, 400);
  const newUserCallbackURL = input.newUserCallbackURL
    ? resolveOAuthCallbackURL(getOAuthCallbackResolverOptions(input.newUserCallbackURL))
    : callbackURL;
  if (!newUserCallbackURL) return c.json({ error: "Invalid newUserCallbackURL" }, 400);

  const returnMode = resolveOAuthReturnMode(input.oauthReturnMode, pendingAuthConfig?.oauth);
  if (!returnMode.ok) return c.json({ error: returnMode.error }, 400);

  let inviteReservation: Extract<OAuthInviteReservation, { ok: true }> | null = null;
  let signUpHandoffToken: string | null = null;
  if (input.requestSignUp && providerRequiresInviteForExplicitSignUp(input.provider)) {
    const reserved = await createOAuthInviteReservation(dbInstance, {
      inviteCode: input.inviteCode,
      provider: input.provider,
    });
    if (!reserved.ok) return c.json({ error: reserved.error }, 403);
    inviteReservation = reserved;
  }
  if (input.requestSignUp && !inviteReservation && pendingAuthConfig?.events?.afterSignUp) {
    const handoff = await createOAuthSignUpHandoff(dbInstance, { provider: input.provider });
    if (!handoff.ok) return c.json({ error: handoff.error }, 500);
    signUpHandoffToken = handoff.token;
  }

  const publicOrigin = resolvePublicAuthOrigin(c, url, [callbackURL, newUserCallbackURL]);
  const handoffURL = buildOAuthHandoffURL(publicOrigin, callbackURL, returnMode.mode);
  const newUserHandoffURL = buildOAuthHandoffURL(publicOrigin, newUserCallbackURL, returnMode.mode, {
    ...(inviteReservation ? { inviteReservationToken: inviteReservation.token } : {}),
    ...(signUpHandoffToken ? { signUpHandoffToken } : {}),
  });

  let authResponse: Response;
  try {
    authResponse = await authInstance.handler(
      new Request(`${publicOrigin}/api/auth/sign-in/social`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          provider: input.provider,
          callbackURL: handoffURL.toString(),
          errorCallbackURL: input.errorCallbackURL,
          newUserCallbackURL: newUserHandoffURL.toString(),
          scopes: input.scopes,
          requestSignUp: input.requestSignUp,
          idToken: input.idToken,
          loginHint: input.loginHint,
          additionalData: input.additionalData,
        }),
      }),
    );
  } catch (error) {
    if (inviteReservation) await releaseOAuthInviteReservation(dbInstance, inviteReservation.token);
    if (signUpHandoffToken) await releaseOAuthSignUpHandoff(dbInstance, signUpHandoffToken);
    throw error;
  }

  // better-auth stores the OAuth CSRF `state` in a short-lived cookie and
  // validates it on the provider callback. This is not an auth session cookie.
  forwardOAuthStateCookieHeaders(c, authResponse);
  const data = (await authResponse.json().catch(() => ({}))) as {
    url?: string;
    error?: string;
    message?: string;
  };
  if (!authResponse.ok && inviteReservation) {
    await releaseOAuthInviteReservation(dbInstance, inviteReservation.token);
  }
  if (!authResponse.ok && signUpHandoffToken) await releaseOAuthSignUpHandoff(dbInstance, signUpHandoffToken);

  return c.json(
    authResponse.ok ? data : sanitizePublicAuthResponsePayload(data, "Authentication failed"),
    authResponse.status as any,
  );
}

/**
 * 사용자 정보로 JWT Access Token을 발급하는 헬퍼.
 */
async function issueJWT(user: { id: string; email: string; name?: string | null; [key: string]: unknown }) {
  const now = Math.floor(Date.now() / 1000);
  const u = user as Record<string, unknown>;
  const payload = {
    ...copySessionUserFieldsForRuntimeIdentity(u),
    ...pickCustomUserFields(u),
    sub: user.id,
    email: user.email,
    name: user.name,
    iat: now,
    exp: now + JWT_EXPIRES_IN_SEC,
    iss: "gencow-auth",
  };
  const secret = getSecretKey().toString("base64");
  const token = await sign(payload, secret, JWT_ALG);
  return { token, expires_in: JWT_EXPIRES_IN_SEC };
}

/**
 * Bearer 헤더 또는 쿠키에서 세션을 가져오는 헬퍼.
 * 우선순위: Bearer header (세션 토큰) > Cookie
 */
async function resolveSessionFromRequest(c: Context): Promise<SessionLookupResult> {
  return resolveRequestSession({
    authorizationHeader: c.req.header("Authorization"),
    cookieHeader: c.req.header("cookie"),
    headers: c.req.raw.headers,
    getSession: (headers) => authInstance.api.getSession({ headers }),
  });
}
async function getSessionFromRequest(c: Context) {
  return (await resolveSessionFromRequest(c)).session;
}

/**
 * better-auth의 모든 auth 엔드포인트 및 커스텀 토큰 발급 라우트 마운트.
 * 커스텀 엔드포인트:
 *   POST /api/auth/token          — 세션 토큰으로 JWT 재발급 (쿠키 또는 Bearer)
 *   POST /api/auth/sign-out       — 세션 종료 (쿠키 또는 Bearer)
 */
export async function betterAuthHandler(c: Context) {
  const url = new URL(c.req.url);
  const pathname = url.pathname;
  const rateLimitResponse = enforceAuthRateLimit(c, pathname);
  if (rateLimitResponse) return rateLimitResponse;

  // ⭐ Lazy Auth: /api/auth/* 요청이 오면 그때 ensureAuth()
  await ensureAuth();

  if (!authInstance) {
    return c.json({ error: "Auth not initialized" }, 500);
  }

  if (c.req.method === "GET" && pathname.endsWith("/social-providers")) {
    return c.json({ providers: listEnabledSocialProviderIds(pendingAuthConfig?.socialProviders) });
  }

  if (c.req.method === "GET" && pathname.endsWith("/sign-in/social/redirect")) {
    try {
      const scopes = url.searchParams.getAll("scope");
      const response = await startSocialSignIn(c, url, {
        provider: url.searchParams.get("provider") ?? undefined,
        callbackURL: url.searchParams.get("callbackURL") ?? undefined,
        oauthReturnMode: url.searchParams.get("oauthReturnMode") ?? undefined,
        errorCallbackURL: url.searchParams.get("errorCallbackURL") ?? undefined,
        newUserCallbackURL: url.searchParams.get("newUserCallbackURL") ?? undefined,
        scopes: scopes.length > 0 ? scopes : undefined,
        requestSignUp: url.searchParams.get("requestSignUp") === "true" ? true : undefined,
        inviteCode: url.searchParams.get("inviteCode") ?? undefined,
      });
      const data = (await response
        .clone()
        .json()
        .catch(() => ({}))) as { url?: string; error?: string };
      if (response.ok && data.url) return c.redirect(data.url, 302);
      const errorCallbackURL = url.searchParams.get("errorCallbackURL");
      if (
        errorCallbackURL &&
        isTrustedOAuthCallbackURL(errorCallbackURL, getOAuthCallbackResolverOptions())
      ) {
        const redirectURL = new URL(errorCallbackURL);
        redirectURL.searchParams.set(
          "error",
          response.status === 403 ? "oauth_invite_invalid" : "oauth_start_failed",
        );
        return c.redirect(redirectURL.toString(), 302);
      }
      return response;
    } catch (error: any) {
      console.error("[auth] social sign-in redirect failed:", redactAuthLogValue(error));
      return c.json({ error: getPublicAuthErrorMessage(error) }, 500);
    }
  }

  if (c.req.method === "POST" && pathname.endsWith("/sign-in/social")) {
    try {
      return startSocialSignIn(c, url, (await c.req.json()) as SocialSignInInput);
    } catch (error: any) {
      console.error("[auth] social sign-in failed:", redactAuthLogValue(error));
      return c.json({ error: getPublicAuthErrorMessage(error) }, 500);
    }
  }

  if (c.req.method === "GET" && pathname.endsWith("/oauth/handoff")) {
    const callbackURL = url.searchParams.get("callbackURL");
    const inviteReservationToken = url.searchParams.get("inviteReservation");
    const signUpHandoffToken = url.searchParams.get("signupHandoff");
    if (!callbackURL || !isTrustedOAuthCallbackURL(callbackURL, getOAuthCallbackResolverOptions())) {
      return c.json({ error: "Invalid callbackURL" }, 400);
    }

    const returnMode = resolveOAuthReturnMode(url.searchParams.get("mode"), pendingAuthConfig?.oauth);
    if (!returnMode.ok) return c.json({ error: returnMode.error }, 400);

    const { session, sessionToken } = await resolveSessionFromRequest(c);
    if (!session?.user) {
      const errorURL = new URL(callbackURL);
      errorURL.searchParams.set("error", "oauth_session_missing");
      return c.redirect(errorURL.toString(), 302);
    }

    if (inviteReservationToken) {
      const finalized = await finalizeOAuthInviteReservation(dbInstance, inviteReservationToken, {
        id: session.user.id,
        email: session.user.email,
      });
      if (!finalized.ok) {
        const errorURL = new URL(callbackURL);
        errorURL.searchParams.set("error", "oauth_invite_invalid");
        return c.redirect(errorURL.toString(), 302);
      }
      await runAfterSignUpHook(session.user, finalized.code, c.req.raw);
    } else if (signUpHandoffToken) {
      const consumed = await consumeOAuthSignUpHandoff(dbInstance, signUpHandoffToken, {
        id: session.user.id,
        email: session.user.email,
      });
      if (!consumed) {
        const errorURL = new URL(callbackURL);
        errorURL.searchParams.set("error", "oauth_signup_invalid");
        return c.redirect(errorURL.toString(), 302);
      }
      await runAfterSignUpHook(session.user, null, c.req.raw);
    }

    if (returnMode.mode === "cookie") {
      const redirectURL = new URL(callbackURL);
      redirectURL.searchParams.set("oauth", "success");
      return c.redirect(redirectURL.toString(), 302);
    }

    if (!sessionToken) {
      const errorURL = new URL(callbackURL);
      errorURL.searchParams.set("error", "oauth_session_missing");
      return c.redirect(errorURL.toString(), 302);
    }

    const code = await createOAuthExchangeCode(dbInstance, {
      sessionToken,
      user: {
        id: session.user.id,
        email: session.user.email,
        name: session.user.name ?? undefined,
      },
    });
    const redirectURL = new URL(callbackURL);
    redirectURL.searchParams.set("code", code);
    return c.redirect(redirectURL.toString(), 302);
  }

  if (c.req.method === "POST" && pathname.endsWith("/oauth/exchange")) {
    const exchangeMode = resolveOAuthReturnMode("token", pendingAuthConfig?.oauth);
    if (!exchangeMode.ok) return c.json({ error: exchangeMode.error }, 403);

    const { code } = (await c.req.json().catch(() => ({}))) as { code?: string };
    if (!code) return c.json({ error: "Code is required" }, 400);

    const record = await consumeOAuthExchangeCode(dbInstance, code);
    if (!record) return c.json({ error: "Invalid or expired OAuth code" }, 401);

    const jwt = await issueJWT(record.user);
    return c.json({
      user: { id: record.user.id, email: record.user.email, name: record.user.name },
      sessionToken: record.sessionToken,
      token: jwt.token,
      expires_in: jwt.expires_in,
    });
  }

  if (c.req.method === "GET" && pathname.endsWith("/me")) {
    c.header("Cache-Control", "no-store");
    try {
      const session = await getSessionFromRequest(c);
      const user = presentSafeAuthSessionUser(session?.user, getCustomUserFieldNames());

      if (!user) {
        return c.json({ error: "Unauthorized: Invalid session" }, 401);
      }

      return c.json({ user });
    } catch (error) {
      console.error("[auth] Session lookup failed:", error);
      return c.json({ error: "Internal Server Error" }, 500);
    }
  }

  if (c.req.method === "POST" && pathname.endsWith("/sign-in") && !pathname.includes("/sign-in/")) {
    try {
      const { email, password } = await c.req.json();
      if (!email || !password) {
        return c.json({ error: "Email and password are required" }, 400);
      }

      const fakeReq = new Request(`${url.origin}/api/auth/sign-in/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const authResponse = await authInstance.handler(fakeReq);

      if (!authResponse.ok) {
        const errData = await authResponse.json().catch(() => ({}));
        return c.json(
          { error: getPublicAuthErrorMessage(errData, "Login failed") },
          authResponse.status as any,
        );
      }

      const sessionToken = extractSessionToken(authResponse);
      if (!sessionToken) {
        return c.json({ error: "Failed to extract session token" }, 500);
      }

      const responseData = (await authResponse.json().catch(() => ({}))) as any;
      const user = responseData.user;
      if (!user) {
        return c.json({ error: "Failed to get user data" }, 500);
      }

      const jwt = await issueJWT(user);

      return c.json({
        user,
        sessionToken,
        token: jwt.token,
        expires_in: jwt.expires_in,
      });
    } catch (error: any) {
      console.error("[auth] sign-in failed:", redactAuthLogValue(error));
      return c.json({ error: getPublicAuthErrorMessage(error) }, 500);
    }
  }

  if (c.req.method === "POST" && pathname.endsWith("/sign-up") && !pathname.includes("/sign-up/")) {
    let reservedInviteCode: string | null = null;
    let keepInviteReservation = false;
    try {
      const { email, password, name, inviteCode } = await c.req.json();
      if (!email || !password) {
        return c.json({ error: "Email and password are required" }, 400);
      }

      if (isInviteOnlyMode()) {
        const inviteResult = await reserveInviteCode(dbInstance, inviteCode);
        if (!inviteResult.ok) {
          return c.json({ error: inviteResult.error }, 403);
        }
        reservedInviteCode = inviteResult.code;
      }

      const fakeReq = new Request(`${url.origin}/api/auth/sign-up/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, name: name ?? email.split("@")[0] }),
      });
      const authResponse = await authInstance.handler(fakeReq);

      if (!authResponse.ok) {
        if (reservedInviteCode) {
          await releaseInviteCode(dbInstance, reservedInviteCode);
          reservedInviteCode = null;
        }
        const errData = await authResponse.json().catch(() => ({}));
        return c.json(
          { error: getPublicAuthErrorMessage(errData, "Signup failed") },
          authResponse.status as any,
        );
      }

      keepInviteReservation = true;
      if (reservedInviteCode) console.log(`[auth] Invite code consumed: ${reservedInviteCode}`);

      const sessionToken = extractSessionToken(authResponse);
      if (!sessionToken) {
        if (hasEmailVerification) {
          const responseData = (await authResponse.json().catch(() => ({}))) as any;
          if (responseData.user) {
            await runAfterSignUpHook(responseData.user, reservedInviteCode, c.req.raw);
          }
          return c.json({
            requiresVerification: true,
            email,
            user: responseData.user
              ? {
                  ...responseData.user,
                }
              : { email },
          });
        }
        return c.json({ error: "Failed to extract session token" }, 500);
      }

      const responseData = (await authResponse.json().catch(() => ({}))) as any;
      const user = responseData.user;
      if (!user) {
        return c.json({ error: "Failed to get user data" }, 500);
      }

      await runAfterSignUpHook(user, reservedInviteCode, c.req.raw);

      const jwt = await issueJWT(user);

      return c.json({
        user,
        sessionToken,
        token: jwt.token,
        expires_in: jwt.expires_in,
      });
    } catch (error: any) {
      if (reservedInviteCode && !keepInviteReservation) {
        await releaseInviteCode(dbInstance, reservedInviteCode);
      }
      console.error("[auth] sign-up failed:", redactAuthLogValue(error));
      return c.json({ error: getPublicAuthErrorMessage(error) }, 500);
    }
  }

  // ─── Custom: /api/auth/token ───
  // Bearer 세션 토큰 또는 쿠키로 JWT 재발급.
  if (c.req.method === "POST" && pathname.endsWith("/token")) {
    try {
      const session = await getSessionFromRequest(c);

      if (!session?.user) {
        return c.json({ error: "Unauthorized: Invalid session" }, 401);
      }

      const jwt = await issueJWT(session.user);
      return c.json({ token: jwt.token, expires_in: jwt.expires_in });
    } catch (error) {
      console.error("[auth] Token generation failed:", redactAuthLogValue(error));
      return c.json({ error: "Internal Server Error" }, 500);
    }
  }

  // ─── better-auth 기본 라우트 위임 ───
  // The Better Auth bearer() plugin translates Bearer session tokens and exposes
  // set-auth-token for plugin routes.
  return authInstance.handler(c.req.raw);
}
